import fallbackImage from '../../../../assets/fallback_comment_section.png';
import fallbackWithoutComments from '../../../../assets/fallback_without_comments.png';
import { AlertModal } from './Comments/AlertModal';
import { Box, Button, Icon, Image, Input, Text } from '@qcx/ui';
import { CommentActions } from './Comments/CommentActions';
import { ComponentProps, useCallback, useEffect, useState } from 'react';
import { faPlusCircle, faUser } from '@fortawesome/pro-regular-svg-icons';
import { formatCommentDate } from '../../../../lib/date-formatter.lib';
import { getAvatarUrl } from '../../../../lib/generate-user-avatar.lib';
import { shallow } from '@qcx/store';
import { useAuthModalStore } from '@kratos/auth';
import { useNewsStore } from '../../news.store';
import { z } from 'zod';

type CommentAuthFallbackProps = ComponentProps<typeof Button>;

export const CommentSection = () => {
  const [isLoading, setIsLoading] = useState(true);
  const toggleAuthModal = useAuthModalStore(s => s.toggle);
  const isAuthenticated = useNewsStore(s => s.isAuthenticated);
  const fetchComments = useNewsStore(s => s.fetchCommentaries);
  const authenticateUser = useNewsStore(s => s.authenticateUser);
  const alertModalIsOpen = useNewsStore(s => s.alertModal?.isOpen, shallow);
  const closeAlertModal = useNewsStore(s => s.closeAlertModal);

  const toggleSigninModal = useCallback(() => {
    toggleAuthModal('signup');
  }, [toggleAuthModal]);

  const handleCloseAlert = useCallback(() => {
    closeAlertModal();
  }, [closeAlertModal]);

  useEffect(() => {
    const hydrateComments = async () => {
      try {
        const userAuthenticated = await authenticateUser();

        if (!userAuthenticated) {
          setIsLoading(false);
          return;
        }

        try {
          await fetchComments();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Erro ao recuperar comentários:', error);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao recuperar informações do usuário:', error);
      } finally {
        setIsLoading(false);
      }
    };

    hydrateComments();
  }, [authenticateUser, fetchComments]);

  if (isLoading) {
    return (
      <Box css={{ width: '100%', textAlign: 'center', py: '$6' }}>
        <Text>Carregando...</Text>
      </Box>
    );
  }

  return (
    <Box css={{ width: '100%' }}>
      <Box
        css={{
          padding: '$10 0 0 0',
          borderTop: '2px solid $separatorColor',
          marginBottom: '$6'
        }}
      >
        <Text
          as="h2"
          css={{ fontSize: '$3xl', marginBottom: '$4' }}
          variant="displayXs"
          weight="bold"
        >
          Comentários
        </Text>

        <Text
          css={{
            color: '$textMuted',
            fontSize: '16px',
            marginBottom: '$8'
          }}
        >
          Os comentários são de responsabilidade exclusiva de seus autores e não
          representam a opinião deste site. Se achar algo que viole os termos de
          uso, denuncie.
        </Text>
      </Box>
      {isAuthenticated ? (
        <>
          <FormComment />
          <UsersComment />
          <AlertModal isOpen={!!alertModalIsOpen} onClose={handleCloseAlert} />
        </>
      ) : (
        <CommentAuthFallback onClick={toggleSigninModal} />
      )}
    </Box>
  );
};

const CommentAuthFallback = ({ ...props }: CommentAuthFallbackProps) => (
  <Box
    css={{
      borderRadius: '8px',
      border: '1px solid #E5E7EB',
      dropShadow: '0px 1px 2px 0px #0000000D',
      padding: '$10',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '$6',
      marginTop: '$6',
      marginBottom: '$10'
    }}
  >
    <Box
      css={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        w: '152px',
        h: '48px'
      }}
    >
      <Image
        width={152}
        height={48}
        alt="Fallback Image Comment"
        src={fallbackImage}
        noAspectRatio
        noBlur
        priority
      />
    </Box>

    <Text
      css={{
        fontSize: '$xl',
        fontWeight: '$regular',
        textAlign: 'center'
      }}
    >
      Acesse sua conta e participe da conversa
    </Text>

    <Button
      {...props}
      css={{
        backgroundColor: '#FE6112',
        color: 'white',
        padding: '14px $4',
        fontSize: '15px',
        fontWeight: '$semibold',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
          bgColor: '#FE6112 !important',
          color: 'white !important',
          opacity: '0.9 !important'
        }
      }}
    >
      Entre e participe da conversa
    </Button>
  </Box>
);

const FormComment = () => {
  const [comment, setComment] = useState('');
  const user = useNewsStore(s => s.userInfo);
  const articleId = useNewsStore(s => s.articleId);
  const commentsData = useNewsStore(s => s.comments);
  const setCommentsData = useNewsStore(s => s.setCommentsData);
  const [isLoading, setIsLoading] = useState(false);
  const openAlertModal = useNewsStore(s => s.openAlertModal);
  const commentSchema = z.object({
    content: z.string().min(2).max(3000)
  });

  const validateComment = commentSchema.safeParse(comment);

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setComment(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateComment && user) {
      try {
        setIsLoading(true);
        const response = await fetch('/api/comments', {
          method: 'POST',
          body: JSON.stringify({
            articleId,
            content: comment
          }),
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const commentData = await response.json();

          setCommentsData({
            commentaries: [commentData, ...commentsData.commentaries],
            meta: commentsData.meta
          });

          setComment('');
        } else {
          openAlertModal({ type: 'sendComment' });
        }
      } catch {
        openAlertModal({ type: 'sendComment' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Box
      css={{ display: 'flex', flexDirection: 'column', gap: '$4', w: '$full' }}
    >
      <Box
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: '$4',
          w: '$full',
          pb: '$10'
        }}
      >
        <form style={{ width: '100%' }} onSubmit={handleSubmit}>
          <Box css={{ display: 'flex', alignItems: 'center', gap: '$4' }}>
            {user?.avatarUrl ? (
              <Image
                css={{
                  borderRadius: '$full',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '$4',
                  minMaxSize: '40px'
                }}
                width={40}
                height={40}
                alt={user.name || 'Usuário'}
                src={user.avatarUrl}
                noAspectRatio
                noBlur
                priority
              />
            ) : (
              <Box
                css={{
                  h: '40px',
                  w: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgColor: '#f1f3f5',
                  borderRadius: '$full'
                }}
              >
                <Icon css={{ color: '$textMuted' }} icon={faUser} size="lg" />
              </Box>
            )}
            <Input
              as="textarea"
              name="comment"
              css={{ w: '$full' }}
              style={{ resize: 'none' }}
              placeholder="Deixe seu comentário"
              value={comment}
              required
              onChange={handleCommentChange}
            />
          </Box>

          <Box
            css={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '$4'
            }}
          >
            <Button
              css={{
                backgroundColor: '#FE6112',
                color: 'white',
                padding: '8px $4',
                fontSize: '15px',
                fontWeight: '$semibold',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                '&:disabled': {
                  backgroundColor: '#FE6112 !important',
                  color: 'white !important',
                  opacity: '0.5 !important',
                  cursor: 'not-allowed !important'
                }
              }}
              disabled={!validateComment || isLoading}
              type="submit"
            >
              {isLoading ? 'Publicando...' : 'Publicar Comentário'}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

type CommentWithUser = {
  id: number;
  content: string;
  createdAt: string | Date;
  userId: number;
  user: {
    name: string | null;
    avatarId?: string | null;
  };
  reactions_count: number;
};

const getCommentTimestamp = (comment: CommentWithUser): string => {
  if (typeof comment.createdAt === 'string') {
    return comment.createdAt;
  }
  return comment.createdAt.toISOString();
};

const UsersComment = () => {
  const commentsData = useNewsStore(s => s.comments);
  const updateCommentsData = useNewsStore(s => s.updateCommentsData);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(
    !!commentsData.meta && commentsData.meta.hasMore
  );
  const [lastCursorTimestamp, setLastCursorTimestamp] = useState<string | null>(
    null
  );
  const articleId = useNewsStore(s => s.articleId);
  const commentaries = commentsData.commentaries;
  const openAlertModal = useNewsStore(s => s.openAlertModal);

  useEffect(() => {
    if (commentaries.length > 0) {
      const lastComment = commentaries.at(-1);

      if (lastComment) {
        setLastCursorTimestamp(getCommentTimestamp(lastComment));
      }
    } else {
      setLastCursorTimestamp(null);
    }
  }, [commentaries]);

  const fetchMoreComments = async () => {
    if (isLoadingMore || !hasMore) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const response = await fetch(
        `/api/articles/${articleId}/comments${
          lastCursorTimestamp ? `?cursor=${lastCursorTimestamp}` : ''
        }`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      const data = await response.json();

      updateCommentsData({
        commentaries: data.commentaries,
        meta: data.meta
      });

      setHasMore(!!data.meta && data.meta.hasMore);
    } catch {
      openAlertModal({ type: 'showMoreComments' });
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (commentaries.length === 0) {
    return (
      <Box
        css={{
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <Box
          css={{
            position: 'relative',
            display: 'flex',
            w: '$full',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '$4'
          }}
        >
          <Image
            css={{
              w: '392px',
              position: 'absolute',
              top: '0',
              left: '0'
            }}
            width={392}
            height={392}
            alt="Fallback Image Comment"
            src={fallbackWithoutComments}
            noAspectRatio
            priority
          />
          <Box
            css={{
              position: 'absolute',
              bottom: '0',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              gap: '$2',
              alignItems: 'center'
            }}
          >
            <Text
              css={{
                color: '#101828',
                fontSize: '$xl',
                fontWeight: '$medium'
              }}
            >
              Ainda não há nenhum comentário
            </Text>
            <Text
              css={{
                color: '#667085',
                fontSize: '$md',
                fontWeight: 'normal'
              }}
            >
              Seja o primeiro a comentar.
            </Text>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '$10',
        pt: '$10',
        borderTop: '2px solid $separatorColor'
      }}
    >
      {commentaries.map(comment => {
        const userInfo = {
          name: comment.user.name || 'Usuário',
          avatarUrl: getAvatarUrl(comment.user.avatarId?.toString() || '')
        };

        return (
          <Box key={comment.id} css={{ display: 'flex', gap: '$3' }}>
            {userInfo.avatarUrl ? (
              <Box css={{ w: '40px', h: '40px' }}>
                <Image
                  css={{
                    padding: '0',
                    borderRadius: '$full',
                    w: '40px',
                    h: '40px'
                  }}
                  width={40}
                  height={40}
                  alt={userInfo.name || 'Usuário'}
                  fit="contain"
                  loading="lazy"
                  src={userInfo.avatarUrl}
                  noBlur
                />
              </Box>
            ) : (
              <Box
                css={{
                  h: '40px',
                  w: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgColor: '#f1f3f5',
                  borderRadius: '$full'
                }}
              >
                <Icon css={{ color: '$textMuted' }} icon={faUser} size="lg" />
              </Box>
            )}
            <Box
              css={{
                w: '$full',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}
            >
              <Box
                css={{
                  display: 'flex',
                  gap: '$2',
                  justifyContent: 'space-between'
                }}
              >
                <Text
                  css={{
                    fontSize: '$sm',
                    fontWeight: '$medium',
                    color: '#334155'
                  }}
                >
                  {userInfo.name}
                </Text>
                <Text css={{ fontSize: '$xs' }}>
                  {formatCommentDate(comment.createdAt.toString())}
                </Text>
              </Box>
              <Box
                css={{
                  p: '10px 14px',
                  w: '$full',
                  bgColor: '#F8FAFC',
                  borderRadius: '0 8px 8px 8px'
                }}
              >
                <Text>{comment.content}</Text>
              </Box>
              <CommentActions
                authorId={comment.userId}
                commentId={comment.id}
                reactionsCount={comment.reactions_count}
              />
            </Box>
          </Box>
        );
      })}

      {hasMore && (
        <Box
          css={{
            display: 'flex',
            justifyContent: 'center',
            mt: '$4',
            w: '$full'
          }}
        >
          <Button
            css={{
              w: '$full',
              backgroundColor: 'transparent',
              color: '#334155',
              padding: '8px $4',
              fontSize: '15px',
              fontWeight: '$semibold',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(254, 97, 18, 0.05)'
              },
              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed'
              }
            }}
            disabled={isLoadingMore}
            onClick={fetchMoreComments}
          >
            <Box css={{ display: 'flex', alignItems: 'center', gap: '$2' }}>
              <Text>
                {isLoadingMore ? 'Carregando...' : 'Ver mais comentários'}
              </Text>
              <Icon icon={faPlusCircle} />
            </Box>
          </Button>
        </Box>
      )}
    </Box>
  );
};
